import React, { useState, useEffect, useCallback } from "react";
import { CalendarDays, Gift, TrendingUp, Layers, RefreshCw, Sparkles } from "lucide-react";
import { getCorporateActions } from "../lib/api";

const KIND_ICON = {
  dividend: Gift,
  split: Layers,
  bonus: Sparkles,
  rights: TrendingUp,
  buyback: TrendingUp,
  other: CalendarDays,
};
const KIND_TONE = {
  dividend: { bg: "bg-emerald-500/10", text: "text-emerald-300", border: "border-emerald-500/30" },
  split: { bg: "bg-cyan-500/10", text: "text-cyan-300", border: "border-cyan-500/30" },
  bonus: { bg: "bg-violet-500/10", text: "text-violet-300", border: "border-violet-500/30" },
  rights: { bg: "bg-amber-500/10", text: "text-amber-300", border: "border-amber-500/30" },
  buyback: { bg: "bg-orange-500/10", text: "text-orange-300", border: "border-orange-500/30" },
  other: { bg: "bg-white/5", text: "text-[var(--text-muted)]", border: "border-white/10" },
};

function fmtDays(n) {
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n < 0) return `${-n}d ago`;
  if (n < 7) return `in ${n}d`;
  if (n < 30) return `in ${n}d`;
  return `in ${Math.floor(n / 30)}mo`;
}

export default function CorporateActionsCard() {
  const [events, setEvents] = useState([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { data } = await getCorporateActions(60);
      setEvents(data.events || []);
      setTotalIncome(data.total_projected_income || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't load events");
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(true); };

  if (loading) {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-4" data-testid="corp-actions-loading">
        <div className="h-4 w-44 bg-white/5 animate-pulse rounded mb-3" />
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-9 bg-white/5 animate-pulse rounded" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-4" data-testid="corp-actions-card">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-emerald-300" />
          <div>
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "Chivo" }}>
              Upcoming events in your portfolio
            </h3>
            <p className="text-[10px] text-[var(--text-muted)]">Dividends, splits, bonuses, rights · next 60 days</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {totalIncome > 0 && (
            <div className="text-right" data-testid="dividend-income-forecast">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Expected dividend income</div>
              <div className="text-sm font-mono font-semibold text-emerald-300">
                ₹{totalIncome.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            </div>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 text-[var(--text-muted)] hover:text-emerald-300 rounded hover:bg-emerald-500/10 transition-colors"
            title="Refresh from NSE"
            data-testid="refresh-corp-actions-btn"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[11px] text-red-400 py-3 text-center">{error}</div>
      )}

      {!error && events.length === 0 && (
        <div className="text-[12px] text-[var(--text-muted)] py-6 text-center" data-testid="corp-actions-empty">
          No corporate actions in the next 60 days for your India holdings.
        </div>
      )}

      {events.length > 0 && (
        <div className="space-y-1.5" data-testid="corp-actions-list">
          {events.map((ev, idx) => {
            const tone = KIND_TONE[ev.kind] || KIND_TONE.other;
            const Icon = KIND_ICON[ev.kind] || CalendarDays;
            const exDate = new Date(ev.ex_date);
            const dateStr = exDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
            const urgent = ev.days_until !== undefined && ev.days_until >= 0 && ev.days_until <= 3;
            const projected = ev.projected_payout || 0;
            const qty = ev.your_quantity || 0;
            const amtPerShare = ev.amount_per_share || 0;
            return (
              <div
                key={`${ev.symbol}-${ev.ex_date}-${idx}`}
                className={`px-2.5 py-2 rounded ${tone.bg} border ${tone.border}`}
                data-testid={`corp-action-${ev.symbol}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <Icon size={13} className={tone.text} />
                    <span className="font-mono font-semibold text-white text-[12px] flex-shrink-0">{ev.symbol}</span>
                    <span className={`text-[10px] uppercase tracking-wider ${tone.text} flex-shrink-0`}>{ev.kind}</span>
                    <span className="text-[11px] text-[var(--text-secondary)] truncate">{ev.subject}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-mono text-[10px] text-[var(--text-muted)]">{dateStr}</span>
                    <span className={`font-mono text-[11px] font-semibold ${urgent ? "text-amber-300" : tone.text}`}>
                      {ev.days_until !== undefined ? fmtDays(ev.days_until) : ""}
                    </span>
                  </div>
                </div>
                {projected > 0 && (
                  <div className="flex items-center justify-between mt-1 pl-6 text-[10px] font-mono" data-testid={`payout-${ev.symbol}`}>
                    <span className="text-[var(--text-muted)]">
                      ₹{amtPerShare}/sh × {qty.toLocaleString("en-IN")} shares
                    </span>
                    <span className="text-emerald-300 font-semibold">
                      → ₹{projected.toLocaleString("en-IN", { maximumFractionDigits: 0 })} expected
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
